/**
 * Tournament Service
 * Gerencia operações de Torneios, Etapas e Inscrições
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { db } from '@/core/services/firebase/config';
import type { 
  Tournament, 
  TournamentStage, 
  TournamentEntry, 
  CatchLog 
} from '@/core/types/tournament';

const TOURNAMENTS_COLLECTION = 'tournaments';
const STAGES_COLLECTION = 'tournament_stages';
const ENTRIES_COLLECTION = 'tournament_entries';
const CATCHES_COLLECTION = 'catch_logs';

// ==================== TOURNAMENTS ====================

export const tournamentService = {
  /**
   * Cria um novo torneio
   */
  async createTournament(data: Omit<Tournament, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const id = `tournament_${Date.now()}`;
    const now = new Date();
    
    const tournament: Tournament = {
      ...data,
      id,
      createdAt: now,
      updatedAt: now,
    };

    await setDoc(doc(db, TOURNAMENTS_COLLECTION, id), {
      ...tournament,
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
    });

    return id;
  },

  /**
   * Busca torneio por ID
   */
  async getTournamentById(id: string): Promise<Tournament | null> {
    const docRef = doc(db, TOURNAMENTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) return null;
    
    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date(),
    } as Tournament;
  },

  /**
   * Lista torneios ativos
   */
  async getActiveTournaments(limitCount = 20): Promise<Tournament[]> {
    const q = query(
      collection(db, TOURNAMENTS_COLLECTION),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      } as Tournament;
    });
  },

  /**
   * Lista torneios por organizador
   */
  async getTournamentsByOrganizer(organizerId: string): Promise<Tournament[]> {
    const q = query(
      collection(db, TOURNAMENTS_COLLECTION),
      where('organizerId', '==', organizerId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      } as Tournament;
    });
  },

  // ==================== STAGES ====================

  /**
   * Cria uma nova etapa de torneio
   */
  async createStage(data: Omit<TournamentStage, 'id'>): Promise<string> {
    const id = `stage_${Date.now()}`;
    
    await setDoc(doc(db, STAGES_COLLECTION, id), {
      ...data,
      id,
      dates: {
        start: Timestamp.fromDate(data.dates.start),
        end: Timestamp.fromDate(data.dates.end),
        registrationDeadline: Timestamp.fromDate(data.dates.registrationDeadline),
      },
    });

    return id;
  },

  /**
   * Lista etapas de um torneio
   */
  async getStagesByTournament(tournamentId: string): Promise<TournamentStage[]> {
    const q = query(
      collection(db, STAGES_COLLECTION),
      where('tournamentId', '==', tournamentId),
      orderBy('dates.start', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        dates: {
          start: data.dates.start?.toDate?.() || new Date(),
          end: data.dates.end?.toDate?.() || new Date(),
          registrationDeadline: data.dates.registrationDeadline?.toDate?.() || new Date(),
        },
      } as TournamentStage;
    });
  },

  /**
   * Busca etapa por ID
   */
  async getStageById(id: string): Promise<TournamentStage | null> {
    const docRef = doc(db, STAGES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) return null;
    
    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      dates: {
        start: data.dates.start?.toDate?.() || new Date(),
        end: data.dates.end?.toDate?.() || new Date(),
        registrationDeadline: data.dates.registrationDeadline?.toDate?.() || new Date(),
      },
    } as TournamentStage;
  },

  // ==================== ENTRIES ====================

  /**
   * Inscreve um atleta em uma etapa
   */
  async registerAthlete(
    tournamentId: string,
    stageId: string,
    athleteId: string,
    athleteCbpeId?: string
  ): Promise<string> {
    const id = `entry_${athleteId}_${stageId}`;
    const now = new Date();

    const entry: TournamentEntry = {
      id,
      tournamentId,
      stageId,
      athleteId,
      athleteCbpeId,
      status: 'pending_payment',
      stats: {
        totalPoints: 0,
        fishCount: 0,
        largestFishSize: 0,
      },
      createdAt: now,
    };

    await setDoc(doc(db, ENTRIES_COLLECTION, id), {
      ...entry,
      createdAt: Timestamp.fromDate(now),
    });

    return id;
  },

  /**
   * Confirma inscrição (após pagamento)
   */
  async confirmEntry(entryId: string, paymentId: string): Promise<void> {
    await updateDoc(doc(db, ENTRIES_COLLECTION, entryId), {
      status: 'confirmed',
      paymentId,
    });
  },

  /**
   * Lista inscrições de uma etapa
   */
  async getEntriesByStage(stageId: string): Promise<TournamentEntry[]> {
    const q = query(
      collection(db, ENTRIES_COLLECTION),
      where('stageId', '==', stageId),
      where('status', 'in', ['confirmed', 'checked_in'])
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
      } as TournamentEntry;
    });
  },

  /**
   * Lista inscrições de um atleta
   */
  async getEntriesByAthlete(athleteId: string): Promise<TournamentEntry[]> {
    const q = query(
      collection(db, ENTRIES_COLLECTION),
      where('athleteId', '==', athleteId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
      } as TournamentEntry;
    });
  },

  /**
   * Verifica se atleta já está inscrito em uma etapa
   */
  async isAthleteRegistered(stageId: string, athleteId: string): Promise<boolean> {
    const entryId = `entry_${athleteId}_${stageId}`;
    const docRef = doc(db, ENTRIES_COLLECTION, entryId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  },

  // ==================== CATCH LOGS ====================

  /**
   * Registra uma captura durante torneio
   */
  async logCatch(data: Omit<CatchLog, 'id' | 'createdAt'>): Promise<string> {
    const id = `catch_${Date.now()}_${data.athleteId}`;
    const now = new Date();

    await setDoc(doc(db, CATCHES_COLLECTION, id), {
      ...data,
      id,
      createdAt: Timestamp.fromDate(now),
      metadata: {
        ...data.metadata,
        capturedAt: Timestamp.fromDate(data.metadata.capturedAt),
      },
    });

    // Atualiza estatísticas da inscrição
    const entryId = data.entryId;
    const entryRef = doc(db, ENTRIES_COLLECTION, entryId);
    const entrySnap = await getDoc(entryRef);
    
    if (entrySnap.exists()) {
      const entryData = entrySnap.data();
      const currentStats = entryData.stats || { totalPoints: 0, fishCount: 0, largestFishSize: 0 };
      
      await updateDoc(entryRef, {
        'stats.fishCount': currentStats.fishCount + 1,
        'stats.largestFishSize': Math.max(currentStats.largestFishSize, data.measurement),
      });
    }

    return id;
  },

  /**
   * Lista capturas de uma inscrição
   */
  async getCatchesByEntry(entryId: string): Promise<CatchLog[]> {
    const q = query(
      collection(db, CATCHES_COLLECTION),
      where('entryId', '==', entryId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        metadata: {
          ...data.metadata,
          capturedAt: data.metadata.capturedAt?.toDate?.() || new Date(),
        },
      } as CatchLog;
    });
  },

  /**
   * Aprova ou rejeita uma captura
   */
  async reviewCatch(
    catchId: string, 
    status: 'approved' | 'rejected', 
    reviewedBy: string,
    rejectionReason?: string
  ): Promise<void> {
    const updateData: Record<string, unknown> = {
      status,
      reviewedBy,
    };
    
    if (rejectionReason) {
      updateData.rejectionReason = rejectionReason;
    }

    await updateDoc(doc(db, CATCHES_COLLECTION, catchId), updateData);
  },
};

export default tournamentService;
